<template>
  <div id="add-user-form">
    <div class="add-user-details">
      <div class="add-users-individual-form col-md-6 col-xs-12">
        <div class="user-details-container">
          <h3>User Details</h3>
          <fieldset class="form-programme form-entry form-required">
            <label for="add-users-name-input">Name</label>
            <input id="add-users-name-input" type="text" class="form-control" placeholder="Enter full name" v-model="formName">
          </fieldset>
          <fieldset class="form-programme form-entry form-required">
            <label for="add-users-email-input">Email</label>
            <input id="add-users-email-input" type="text" class="form-control" placeholder="Enter email address" v-model="formEmail">
          </fieldset>

          <fieldset v-if="programmeHasGroups" class="form-programme form-entry">
            <label for="tag-input">Groups</label>
            <span class="question-icon">
              <k-tooltip
                :text="'Students will only be able to access module assets that have no groups or are in the same group(s) as the student.'"
                trigger-label="More information about Groups"
              >
                <sup><i class="fas fa-question-circle" aria-hidden="true"></i></sup>
              </k-tooltip>
            </span>
            <div v-if="programme.programme_groups && programme.programme_groups.length > 0">
              <word-tags label='' v-model="formGroups" textInputPlaceholder="Enter tags" :options="programme.programme_groups"></word-tags>
            </div>
          </fieldset>

          <fieldset class="form-programme form-entry">
            <label>Domain</label>
            <span class="question-icon">
              <k-tooltip
                :text="`This is the domain the students sign in link will redirect to. For example, the domain <b>'imc'</b> would point to imc.edukate.ai. The default is app.edukate.ai.`"
                trigger-label="More information about domain"
              >
                <sup><i class="fas fa-question-circle" aria-hidden="true"></i></sup>
              </k-tooltip>
            </span>
            <k-dropdown id="add-domain-input" type="text" placeholder="Domain" :options="domainOptions" v-model="formDomain" :showSearch="true"></k-dropdown>
          </fieldset>
          <fieldset class="form-programme form-entry">
            <label for="add-company-input">Company</label>
            <span class="question-icon">
              <k-tooltip
                :text="`This is the company the student belongs to or is affiliated with.`"
                trigger-label="More information about company"
              >
                <sup><i class="fas fa-question-circle" aria-hidden="true"></i></sup>
              </k-tooltip>
            </span>
            <k-dropdown id="add-company-input" type="text" placeholder="Company" :options="employerOptions" v-model="formEmployer" :showSearch="true"></k-dropdown>
          </fieldset>

          <fieldset class="form-programme form-entry">
            <label for="add-users-line-manager-name-input">Line Manager Name</label>
            <input id="add-users-line-manager-name-input" type="text" class="form-control" placeholder="Enter manager's name" v-model="formLineManagerName">
          </fieldset>
          <fieldset class="form-programme form-entry">
            <label for="add-users-line-manager-email-input">Line Manager Email</label>
            <input id="add-users-line-manager-email-input" type="text" class="form-control" placeholder="Enter manager's email" v-model="formLineManagerEmail">
          </fieldset>
        </div>

        <div class="form-buttons">
          <button class="btn btn-primary" id="add-user-individual-button" @click="addUser" :disabled="!validForm">
            <i class="fas fa-user-plus" aria-hidden="true"></i> Add User
          </button>
          <button class="btn btn-danger" id="remove-user-remove-button" @click="openClearWarning" :disabled="users.length === 0">
            <i class="fas fa-times-circle" aria-hidden="true"></i> Clear All
          </button>
        </div>
      </div>

      <div class="add-users-dropfield-container col-md-6 col-xs-12">
        <h3>Bulk Import</h3>
        <dropfield class="users-dropfield"
          v-on:file="eventGetFile"
          v-bind:customMessage="customMessage"
          url=""
          accept=".csv"
          v-bind:immediatePost="false">
        </dropfield>
      </div>
    </div>
    <div class="add-user-error" v-if="errors.length > 0">
      <div class="col-md-12 col-xs-12">
        <div class="k-alert-danger">
          <strong> Errors found: </strong>
          <ul>
            <li v-for="err in errors" class="error-entry-li">
              {{err}}
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div v-if="refinedUsers.length" class="add-user-table" id="k-add-user-table-container">
      <k-table :headers="kableHeaders" :rows="refinedUsers" :max="10" :hideable="false" :panel="false" @clicked="removeUser"></k-table>
      <button id="k-user-add-review-button" class="btn btn-primary" @click="goToReview" :disabled="!readyForReview">Review Users <i class="fas fa-arrow-right" aria-hidden="true"></i></button>
    </div>
    <k-modal :show="showClearWarning" @close="closeClearWarning">
      <template #header>
        <h3>Clear All Users?</h3>
      </template>
      <template #body>
        This will remove all users currently loaded in the form. Are you sure you want to remove them?
      </template>
      <template #footer>
        <button class="btn btn-danger" @click="clearUsers"><i class="fas fa-times-circle" aria-hidden="true"></i> Clear All Users</button>
        <button class="btn btn-primary" @click="closeClearWarning"><i class="fas fa-arrow-left" aria-hidden="true"></i> Go Back</button>
      </template>
    </k-modal>
  </div>
</template>

<style>
#k-add-user-table-container .k-table-container {
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 2px solid var(--kate-background-body-alpha);
}

#add-user-form .users-dropfield {
  width: 100%;
  height: 100%;
  padding-top: 25px;
}

#add-user-form .users-dropfield > form {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 15px;
}

#add-user-form .k-dropbox {
  flex: 1;
}

#add-user-form .k-dropbox-legacy-upload {
  margin: 0;
}

#add-user-form .k-dropbox label p {
  font-size: 1rem;
}
</style>

<style scoped>
.add-user-details {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 30px;
}

.add-users-individual-form {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.add-users-individual-form h3 {
  flex: 1 1 100%;
}

.add-users-dropfield-container {
  flex: 1;
}

label {
  margin-bottom: 0;
}

.question-icon {
  margin-left: 5px;
}

#k-add-user-table-container {
  display: inline-block;
  width: 100%;
}

#k-user-add-review-button {
  float: right;
}

.form-programme {
  margin: 10px 0;
}

.form-buttons {
  display: flex;
  justify-content: space-between;
  margin-top: 25px;
}

.user-details-container {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  justify-content: space-between;
}

.user-details-container > fieldset {
  flex: 1 1 48%;
}
</style>

<script>
import Dropfield from '@base-components/dropfield.vue';
import TimeMixin from '@mixins/time-mixins';
import CsvMixin from '@mixins/csv-mixins';
import KTable from '@base-components/k-table.vue';
import KModal from '@base-components/k-modal.vue';
import KDropdown from '@base-components/k-dropdown.vue';
import { KTooltip } from '@kate/components';
import WordTags from '../../components/word-tags.vue';

const EMAIL_REGEX = /.+@.+\..+/;
const DOMAINS = [
  { name: 'Default', domain: undefined, example: 'app.edukate.ai' },
  { name: 'IMC', domain: 'imc', example: 'imc.edukate.ai' },
];

export default {
  props: {
    programme: {
      type: Object,
    },
    baseUsers: {
      type: Array,
      default: () => [],
    },
  },
  mixins: [TimeMixin, CsvMixin],
  components: {
    WordTags,
    dropfield: Dropfield,
    'k-table': KTable,
    'k-modal': KModal,
    'k-dropdown': KDropdown,
    KTooltip,
  },
  data() {
    return {
      users: [],
      showClearWarning: false,
      formEmail: '',
      formName: '',
      formLineManagerName: '',
      formLineManagerEmail: '',
      formGroups: [],
      formDomain: undefined,
      formEmployer: undefined,
      employers: [],
      uploadedFile: null,
      customMessage: `<h4>Drag csv file with the following format:</h4>
      <div>
      <br>
        <p>name, email, groups (optional), domain (optional), company (optional), line_manager_full_name (optional), line_manager_email (optional)</p>
        <p>John Smith, john.smith@fakemail.com, Group A, , , John LM, john.lm@fakemail.com</p>
        <p>Jane Doe, john.smith@fakemail.com, "Group A, Group B", , , Jane LM, jane.lm@fakemail.com</p>
        <p>Donna Noble, donna.noble@fakemail.com, ,imc</p>
      </div>
      <br>
      <i>Note that groups are case-sensitive</i>
      `,
      sendEmails: false,
      showChangeProgDialog: false,
      errors: [],
    };
  },
  beforeMount() {
    this.users = this.baseUsers;
    this.getEmployers();
  },
  computed: {
    readyForReview() {
      return this.users.length > 0 && this.programme !== undefined;
    },
    refinedUsers() {
      return this.users.map((val, index) => ({
        action: {
          key: index,
          text: '<div style="text-align: center; color: var(--kate-danger)"><i class="fas fa-minus-circle" aria-hidden="true"></i></div>',
        },
        ...val,
        groups: {
          key: index,
          tags: val.groups,
        },
      }));
    },
    kableHeaders() {
      const headers = {
        name: {
          name: 'Name',
          filterable: true,
        },
        email: {
          name: 'Email',
          filterable: true,
        },
        lineManagerName: {
          name: 'Line Manager Name',
          filterable: true,
        },
        lineManagerEmail: {
          name: 'Line Manager Email',
          filterable: true,
        },
        groups: {
          name: 'Groups',
          filterable: true,
          type: 'tags',
        },
        domainName: {
          name: 'Domain',
          filterable: true,
        },
        employerName: {
          name: 'Company',
          filterable: true,
        },
        action: {
          name: 'Review User',
          type: 'action',
        },
      };
      if (!this.programmeHasGroups) {
        delete headers.groups;
      }
      return headers;
    },
    programmeHasGroups() {
      return Boolean(this.programme?.programme_groups && this.programme?.programme_groups.length > 0);
    },
    validEmail() {
      return this.validateEmail(this.formEmail);
    },
    validName() {
      return this.validateName(this.formName);
    },
    validLineManagerName() {
      if (!this.formLineManagerName) return true;
      return this.validateName(this.formLineManagerName);
    },
    validLineManagerEmail() {
      if (!this.formLineManagerEmail) return true;
      return this.validateEmail(this.formLineManagerEmail);
    },
    validForm() {
      return this.validEmail && this.validName && this.validLineManagerName && this.validLineManagerEmail;
    },
    domainOptions() {
      return DOMAINS.map(x => x.domain || 'default');
    },
    selectedDomain() {
      const dom = DOMAINS.find(x => x.domain === this.formDomain);
      return dom ? dom.domain : undefined;
    },
    selectedDomainName() {
      const dom = DOMAINS.find(x => x.domain === this.formDomain);
      return dom ? dom.name : 'Default';
    },
    employerOptions() {
      return this.employers.map(x => x.name);
    },
    selectedEmployerId() {
      const dom = this.employers.find(x => x.name === this.formEmployer);
      return dom ? dom.id : undefined;
    },
    selectedEmployer() {
      const dom = this.employers.find(x => x.name === this.formEmployer);
      return dom ? dom.name : undefined;
    },
  },
  methods: {
    checkEmailInUsers(email) {
      return this.users.filter(user => user.email === email).length > 0;
    },
    goToReview() {
      if (!this.readyForReview) {
        return;
      }
      this.$emit('useradded', this.users);
    },
    openClearWarning() {
      this.showClearWarning = true;
    },
    closeClearWarning() {
      this.showClearWarning = false;
    },
    removeUser(index) {
      this.users.splice(index, 1);
    },
    clearUsers() {
      this.users = [];
      this.closeClearWarning();
    },
    addUser() {
      if (!this.validForm) {
        return;
      }
      if (this.checkEmailInUsers(this.formEmail)) {
        this.$ktoast.showError('Email already in form');
        return;
      }
      this.users.push({
        name: this.formName.trim(),
        email: this.formEmail.trim(),
        lineManagerName: this.formLineManagerName.trim(),
        lineManagerEmail: this.formLineManagerEmail.trim(),
        groups: this.formGroups,
        domainName: this.selectedDomainName,
        domain: this.selectedDomain,
        employerName: this.selectedEmployer,
        employer_id: this.selectedEmployerId,
      });
      this.formName = '';
      this.formEmail = '';
      this.formLineManagerName = '';
      this.formLineManagerEmail = '';
      this.formGroups = [];
      this.formDomain = undefined;
      this.formEmployer = undefined;
    },
    validateRegex(name, regex) {
      return regex.test(name);
    },
    validateEmail(name) {
      return this.validateRegex(name, EMAIL_REGEX);
    },
    validateName(name) {
      return name.trim().length > 0;
    },
    validateGroups(groups) {
      let allValid = true;
      groups.forEach(group => {
        if (!this.programme?.programme_groups.map(g => g.name).includes(group)) {
          allValid = false;
        }
      });
      return allValid;
    },
    validateFields(fields) {
      const requiredFields = ['name', 'email'];
      const optionalFields = ['domain', 'company', 'groups', 'line_manager_full_name', 'line_manager_email'];
      const allRequired = requiredFields.every(x => fields.includes(x));
      const otherFields = fields.filter((x, idx, arr) => arr.indexOf(x) === idx && optionalFields.includes(x));
      return allRequired && (fields.length === (otherFields.length + requiredFields.length));
    },
    eventGetFile(file) {
      this.errors = [];
      this.uploadedFile = file;
      this.parseCsvFile(file[0], results => {
        if (results.errors.length) {
          this.errors.push('Error parsing file. Is the file a csv file?');
          return;
        }
        if (!this.validateFields(results.meta.fields)) {
          this.errors.push('Incorrect headers. Need either ("name", "email") plus any of the optional columns ("domain", "company", "groups", "line_manager_full_name", "line_manager_email") only');
          return;
        }
        if (!this.programme?.programme_groups && results.meta.fields.includes('groups')) {
          this.errors.push('Groups column not allowed for this programme');
          return;
        }
        const emails = [];
        let hasErrors = false;
        let datum;
        const resultDomains = results.data.map(x => DOMAINS.find(y => y.domain === x.domain));
        const resultEmployers = results.data.map(x => this.employers.find(y => y.name === x.company));
        for (let i = 0; i < results.data.length; i++) {
          datum = results.data[i];
          datum.email = (datum.email || '').trim();
          datum.name = (datum.name || '').trim();
          datum.line_manager_full_name = (datum.line_manager_full_name || '').trim();
          datum.line_manager_email = (datum.line_manager_email || '').trim();
          datum.groups = datum.groups && datum.groups.length > 0 ? datum.groups.split(',').map(g => g.trim()) : [];
          if (!this.validateEmail(datum.email)) {
            this.errors.push(this.formatError('Error validating email', datum.name, datum.email, i));
            hasErrors = true;
          }
          if (!this.validateName(datum.name)) {
            this.errors.push(this.formatError('Error validating name', datum.name, datum.email, i));
            hasErrors = true;
          }
          if (datum.line_manager_email && !this.validateEmail(datum.line_manager_email)) {
            this.errors.push(this.formatError('Error validating line manager email', datum.line_manager_full_name, datum.line_manager_email, i));
            hasErrors = true;
          }
          if (datum.line_manager_full_name && !this.validateName(datum.line_manager_full_name)) {
            this.errors.push(this.formatError('Error validating line manager name', datum.line_manager_full_name, datum.line_manager_email, i));
            hasErrors = true;
          }
          if (!this.validateGroups(datum.groups)) {
            this.errors.push(this.formatError('Group(s) not in the programme', datum.name, datum.email, i));
            hasErrors = true;
          }
          if (emails.indexOf(datum.email) !== -1 || this.checkEmailInUsers(datum.email)) {
            this.errors.push(this.formatError('Duplicate Email', datum.name, datum.email, i));
            hasErrors = true;
          }
          datum.domain = resultDomains[i] ? resultDomains[i].domain : undefined;
          datum.domainName = resultDomains[i] ? resultDomains[i].name : 'Default';
          datum.employer_id = resultEmployers[i] ? resultEmployers[i].id : undefined;
          datum.employerName = resultEmployers[i] ? resultEmployers[i].name : undefined;
          emails.push(datum.email);
        }
        if (!hasErrors) {
          this.users = this.users.concat(results.data.map(user => ({
            ...user,
            lineManagerName: user.line_manager_full_name,
            lineManagerEmail: user.line_manager_email,
          })));
        }
      });
    },
    formatError(msg, name, email, row) {
      return `Row ${row}:  ${msg} | ${name}, ${email}`;
    },
    getEmployers() {
      this.$http.get('/api/profile/employers').then(res => {
        this.employers = res.data;
      }).catch(err => {
        this.$logger.autowarn('There was an error while attempting to retrieve employers in add user', undefined, err);
        if (!this.$http.isWarning(err)) {
          this.$ktoast.showError(err);
        }
      });
    },
  },
};
</script>
